import CryptoJS from 'crypto-js';
import { setOpenSnackBar } from 'src/redux/siteAuditReducer';
import { store } from 'src/redux/store';
import {
  getCookie,
  localStorageMethod,
  setRequestGscURL
} from './FunctionHelper';
import { localseoSignupSqspFlow, paths, planNameCookie } from './TextMessage';
import {
  setIsUnlocked,
  setIsUnlockedBackLink,
  setSearchesDetails
} from 'src/redux/keywordResearch';
import { setIsLocalSeoUnlocked } from 'src/redux/localSeo';

export const decryptCardToken = (encryptedToken, secretKey) => {
  const bytes = CryptoJS.AES.decrypt(encryptedToken, secretKey);
  const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  return decryptedData;
};

export const handleSnackBar = () => {
  store.dispatch(setOpenSnackBar(true));
};

/**
 * Get expiration time for the cookie (30 days from now)
 *
 * @returns {string} Expiration time as a string
 */
const getExpTime = () => {
  const date = new Date();
  date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days expiration
  return date.toUTCString();
};

/**
 * Set a UTM cookie with a specified name and value
 *
 * @param {string} name - The name of the cookie
 * @param {string} value - The value to be set for the cookie
 */
export const setUtmCookie = (name, value) => {
  const expTime = getExpTime();
  const cookieString = `${name}=${value};expires=${expTime};path=/;secure`;
  document.cookie = cookieString;
};

/**
 * Remove a cookie with a specified name
 *
 * @param {string} name - The name of the cookie to be removed
 */
export const removeUtmCookie = (name) => {
  const cookieString = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;secure`;
  document.cookie = cookieString;
};

/**
 * Removes the negative sign from a number or a numeric string if present.
 *
 * @param {number | string} value - The input value to process.
 * @returns {string} The absolute value of the input as a string.
 * @throws {Error} If the input is neither a number nor a valid numeric string.
 */
export const removeNegativeSign = (value: number | string): string => {
  // Convert numeric strings to numbers and validate the input
  const numericValue = typeof value === 'number' ? value : parseFloat(value);

  // Check for valid numeric input
  if (isNaN(numericValue)) {
    console.error(
      `Invalid input: ${value}. Expected a number or numeric string.`
    );
  }

  // Return the absolute value as a string
  return Math.abs(numericValue).toString();
};

export const capitalizeFirstLetter = (value) => {
  if (typeof value !== 'string' || !value) {
    return ''; // Return an empty string for invalid inputs
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const getScreenResolution = () => {
  try {
    if (window.innerWidth && window.innerHeight) {
      // If it's a mobile/tablet screen (width < 1024px), return a standard resolution
      if (window.innerWidth < 1024) {
        return '1920x1080';
      }
      return `${window.innerWidth}x${window.innerHeight}`;
    }
    // Fallback value if window is undefined (e.g., SSR or errors)
    return '1920x1080';
  } catch (error) {
    console.error('Error getting screen resolution:', error);
    return '1920x1080'; // Safe fallback resolution
  }
};

export const convertTimestampToDateObject = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1, // JS months are 0-based
    day: date.getDate()
  };
};

export const getTimeAgo = (input: string | number | Date): string => {
  try {
    if (!input) return '';

    const date = new Date(input);
    const now = new Date();

    if (isNaN(date.getTime())) return '';

    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (seconds < 0) return '';

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(seconds / 86400);

    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

    // Use actual calendar difference for months and years
    const nowYear = now.getFullYear();
    const nowMonth = now.getMonth();
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();

    const totalMonths = (nowYear - dateYear) * 12 + (nowMonth - dateMonth);

    if (totalMonths < 12) {
      return `${totalMonths} month${totalMonths === 1 ? '' : 's'} ago`;
    }

    const years = Math.floor(totalMonths / 12);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  } catch (error) {
    console.error('getTimeAgo error:', error);
    return '';
  }
};

export const getLocalSeoId = () => {
  const id = localStorageMethod('get', 'local_seo_id') || null;
  return id;
};

export const setLocalSeoId = (id) => {
  localStorageMethod('set', 'local_seo_id', id);
};

export const getLocalSeoBusinessId = () => {
  const id = localStorageMethod('get', 'local_seo_business_id') || null;
  return id;
};

export const setLocalSeoBusinessId = (id) => {
  localStorageMethod('set', 'local_seo_business_id', id);
};

export const removeLocalSeoAllStorage = () => {
  localStorageMethod('remove', 'local_seo_id');
  localStorageMethod('remove', 'local_seo_business_id');
};

export const getBaseDomainName = (url) => {
  if (!url) return null;

  let hostname = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
  const parts = hostname.split('.');

  return parts[0] || null;
};

/**
 * Extract clean domain name (without www or TLD)
 *
 * @param site input URL string
 * @returns domain name (e.g. "justemase" from "http://justemase.com")
 */
export const getDomainName = (site: string): string => {
  try {
    if (!site) return '';

    // Ensure valid protocol
    const url =
      site.startsWith('http://') || site.startsWith('https://')
        ? site
        : `http://${site}`;

    const hostname = new URL(url).hostname.toLowerCase(); // normalize case
    const cleanHost = hostname.replace(/^www\./, '');

    // Split by '.' and take the second-to-last part (root domain)
    const parts = cleanHost.split('.');
    if (parts.length > 2) {
      // e.g. sub.jackeames.com -> jackeames
      return parts[parts.length - 2];
    }
    return parts[0];
  } catch (error) {
    console.error('Invalid URL provided:', site, error);
    return '';
  }
};

export const getSiteName = (url) => {
  if (!url) return null;

  let hostname = url.replace(/^https?:\/\//, '');
  hostname = hostname.replace(/^www\./, '');

  return hostname;
};

/**
 * Get expiration date string (30 days from now)
 */
const getLocalSeoExpTime = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 30); // 30 days ahead
  return date.toUTCString();
};

/**
 * Set or update Local SEO user cookie
 *
 * Stores a simple flag instead of token.
 */
export const setLocalSeoCookie = (): void => {
  try {
    const expTime = getLocalSeoExpTime();
    const cookieString = `localseo_user_session=true;expires=${expTime};path=/;secure;SameSite=Strict`;
    document.cookie = cookieString;
  } catch (error) {
    console.error('Error setting Local SEO cookie:', error);
  }
};

/**
 * Get Local SEO cookie value
 *
 * @returns "true" | null
 */
export const getLocalSeoCookieValue = (): string | null => {
  try {
    return (
      document?.cookie
        ?.split('; ')
        ?.find((cookie) => cookie.startsWith('localseo_user_session='))
        ?.split('=')[1] ?? null
    );
  } catch (error) {
    console.error('Error getting Local SEO cookie:', error);
    return null;
  }
};

/**
 * Check if Local SEO cookie exists
 *
 * @returns boolean
 */
export const hasLocalSeoCookie = (): boolean => {
  try {
    return getLocalSeoCookieValue() === 'true';
  } catch (error) {
    console.error('Error checking Local SEO cookie:', error);
    return false;
  }
};

/**
 * Remove Local SEO cookie
 */
export const removeLocalSeoCookie = (): void => {
  try {
    document.cookie = `localseo_user_session=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;secure;SameSite=Strict`;
  } catch (error) {
    console.error('Error removing Local SEO cookie:', error);
  }
};

/**
 * @function removeLocalSeoSqSpCookies
 * @description
 * Clears all Squarespace-related Local SEO onboarding data from local storage and cookies.
 *
 * This utility is used when a user skips or disconnects the Squarespace onboarding flow
 * for the Local SEO integration. It ensures no residual Squarespace connection data remains
 * in the browser that could interfere with new connections or onboarding flows.
 *
 * @example
 * // When a user skips the onboarding process:
 * removeLocalSeoSqSpData();
 *
 * @returns {void} This function does not return anything.
 */
export const removeLocalSeoSqSpData = () => {
  localStorageMethod('remove', 'squareSpaceSite');
  localStorageMethod('remove', 'squareSpaceSiteId');
  localStorageMethod('remove', 'squareSpaceAccountName');
  localStorageMethod('remove', 'squareSpaceAccountId');
  localStorageMethod('remove', 'is_local_seo_sqsp_flow');
  removeUtmCookie(localseoSignupSqspFlow);
};

export const connectGsc = (url) => {
  localStorage.setItem('GSCRequestURL', url);
  setRequestGscURL(paths?.google, true);
};

export const parseRatingValue = (value: unknown): number | '' => {
  if (value === null || value === undefined || value === '') return '';
  const parsed = parseFloat(String(value));
  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
    return '';
  }
  return parsed;
};

/**
 * Checks if a given value is a valid number and not zero.
 *
 * This utility is useful when you want to conditionally render
 * numeric values in React, avoiding rendering for `0`, `null`, `undefined`,
 * or invalid numbers (NaN).
 *
 * @param value - The value to check. Can be a number, string, or any type.
 * @returns {number | null} - Returns the numeric value if valid and not zero, otherwise returns null.
 *
 * @example
 * const val1 = shouldRenderNumber(5);       // returns 5
 * const val2 = shouldRenderNumber("12");    // returns 12
 * const val3 = shouldRenderNumber("0");     // returns null
 * const val4 = shouldRenderNumber(null);    // returns null
 * const val5 = shouldRenderNumber("abc");   // returns null
 *
 * // Usage in JSX (just an example)
 * {shouldRenderNumber(modal?.visibilityDiff) && getCompareScoreJSX(shouldRenderNumber(modal?.visibilityDiff))}
 */
export const shouldRenderNumber = (value: any): number | null => {
  try {
    const num = Number(value); // convert input to a number
    // Only return the number if it is valid (not NaN) and not zero
    return !isNaN(num) && num !== 0 ? num : null;
  } catch (error) {
    // Fallback in case of unexpected errors
    return null;
  }
};

/**
 * Filters an array of strings to return only unique items, ignoring case and whitespace.
 *
 * @param {string[]} items - The array of strings to filter.
 * @returns {string[]} A new array containing unique, non-empty strings.
 *
 * @example
 * getUniqueItems([' SEO ', 'seo', 'Marketing']) // returns [' SEO ', 'Marketing']
 */
export const getUniqueItems = (items: string[]): string[] => {
  // Error handling: Ensure input is an array
  if (!Array.isArray(items)) {
    return [];
  }

  const seen = new Set<string>();

  return items?.filter((item) => {
    // Safety check: Ensure item is a string
    if (typeof item !== 'string') return false;

    // Normalize: trim whitespace and convert to lowercase for comparison
    const normalized = item?.trim()?.toLowerCase();

    // Filter out empty strings or duplicates
    if (!normalized || seen.has(normalized)) {
      return false;
    }

    // Mark this normalized value as seen
    seen.add(normalized);
    return true;
  });
};

export const getDomainOnly = (url?: string | null): string | null => {
  if (!url || typeof url !== 'string') return null;

  try {
    // Ensure URL has protocol for the URL constructor
    const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;

    const { hostname } = new URL(normalizedUrl);

    // Remove leading "www."
    return hostname.replace(/^www\./, '') || null;
  } catch (error) {
    return null;
  }
};

/**
 * Generates a Google S2 favicon URL from a domain.
 *
 * @param domain - Full URL or domain (e.g., "https://www.fiverr.com/blogs")
 * @param size - Optional favicon size, default = 64
 * @returns string - favicon endpoint URL
 */
export const getFaviconUrl = (domain: string, size: number = 32): string => {
  try {
    if (!domain || typeof domain !== 'string') return '';

    // Extract base domain if full URL is provided
    const sanitizedDomain = domain
      ?.replace(/^https?:\/\//i, '')
      ?.replace(/^www\./i, '')
      ?.split('/')[0]; // Remove paths like /blog, /pages

    if (!sanitizedDomain?.includes('.')) return '';

    return `https://www.google.com/s2/favicons?sz=${size}&domain=${sanitizedDomain}`;
  } catch (err) {
    return '';
  }
};

export const getPlanNameFromCookie = (): string | null => {
  try {
    return getCookie(planNameCookie) || null;
  } catch (error) {
    return null;
  }
};

/**
 * Updates the user searches details and feature unlock states in Redux.
 * 
 * @param {any} getSearchesDetail - The credit/searches details object.
 */
export const updateSearchesDetailsAndLimits = (getSearchesDetail: any) => {
  if (getSearchesDetail) {
    store.dispatch(setSearchesDetails(getSearchesDetail));
    store.dispatch(setIsUnlocked(getSearchesDetail?.keywordLimit > 0));
    store.dispatch(setIsUnlockedBackLink(getSearchesDetail?.backlinkLimit >= 100));
    store.dispatch(setIsLocalSeoUnlocked(getSearchesDetail?.mapRankLimit > 0));
  }
};

